// Derivación determinística de la pseudo-cuenta de una (syphrax_id, freq_secret).
//
// Filosofía Syphrax: dentro de una frecuencia secreta el usuario aparece ante el
// servidor como UNA CUENTA INDEPENDIENTE de su cuenta base. El server no puede
// asociar la pseudo-cuenta con la cuenta base — ve dos rows distintas en `accounts`
// y no tiene forma de saber que pertenecen a la misma persona física.
//
// Tanto Alice como Bob (que comparten freq_secret) pueden computar el pseudónimo
// del otro a partir de su syphrax_id de general → eso es lo que permite enrutar
// mensajes dentro de la freq SIN que el server conozca la relación.
//
// IMPORTANTE: las funciones aquí son SÍNCRONAS. El caller debe haber llamado a
// `crypto.ready()` antes (Auth.tsx ya lo hace al montar).

import sodium from 'libsodium-wrappers-sumo';
import * as crypto from './crypto';

// Cache del Argon2id de freq_secret. Sin esto, cada pseudonymOf/deriveFrequencyAccount
// ejecuta crypto_pwhash MODERATE (256 MiB, 3 ops) que en móvil tarda 1-3s. Con cache
// solo se hace 1 vez por freq_secret en la vida de la sesión.
const freqKeyCache = new Map<string, Uint8Array>();
function cachedFreqKey(freqSecret: string): Uint8Array {
  let k = freqKeyCache.get(freqSecret);
  if (!k) {
    k = crypto.deriveFrequencyKey(freqSecret);
    freqKeyCache.set(freqSecret, k);
  }
  return k;
}

// Cache adicional del resultado final de pseudonymOf por (syphraxId, freqSecret).
// Aún más rápido — evita HKDF y base32 también.
const pseudoCache = new Map<string, string>();

export interface FrequencyAccount {
  /** Username público de la pseudo-cuenta (lo que ve el server). */
  pseudonym: string;
  /** Password con el que la pseudo-cuenta hace login en el server. */
  authPassword: string;
  /** Salt de la pseudo-cuenta (registrado en server por compat con el flow de auth). */
  pseudoSalt: Uint8Array;
  /** Keypair X25519 (la "identity" cripto de la pseudo-cuenta). */
  publicKey: Uint8Array;
  privateKey: Uint8Array;
  /** Clave para envolver blobs (chats, prefs, ratchet sessions) de la pseudo-cuenta. */
  wrappingKey: Uint8Array;
  /** Signing keypair Ed25519 (cuando se reactive el ratchet). */
  signingPublic: Uint8Array;
  signingPrivate: Uint8Array;
}

const PSEUDO_INFO_PREFIX = 'syphrax-pseudo-account-v1::';

/**
 * Deriva el material completo para la pseudo-cuenta del propio usuario
 * en la frecuencia indicada. Necesita el syphrax_id propio + el secreto
 * de la frecuencia. Resultado determinístico: misma entrada → misma cuenta.
 */
export function deriveFrequencyAccount(
  syphraxId: string,
  freqSecret: string,
): FrequencyAccount {
  const seed = cachedFreqKey(freqSecret); // 32 B Argon2id (cached)
  const info = new TextEncoder().encode(PSEUDO_INFO_PREFIX + syphraxId);
  const material = crypto.hkdf(seed, new Uint8Array(0), info, 176);

  const pseudoBytes = material.slice(0, 16);
  const pseudonym = base32NoPad(pseudoBytes);
  const authPasswordBytes = material.slice(16, 48);
  const authPassword = crypto.b64encode(authPasswordBytes);
  const x25519Seed = material.slice(48, 80);
  const wrappingKey = material.slice(80, 112);
  const ed25519Seed = material.slice(112, 144);
  const pseudoSalt = material.slice(144, 176);

  // X25519 keypair determinístico desde seed.
  const x = sodium.crypto_kx_seed_keypair(x25519Seed);
  // Ed25519 keypair determinístico desde seed (32 bytes).
  const ed = sodium.crypto_sign_seed_keypair(ed25519Seed);

  return {
    pseudonym,
    authPassword,
    pseudoSalt,
    publicKey: x.publicKey,
    privateKey: x.privateKey,
    wrappingKey,
    signingPublic: ed.publicKey,
    signingPrivate: ed.privateKey,
  };
}

/**
 * Computa el pseudónimo (username opaco) que un peer concreto tiene dentro de
 * esta frecuencia. Ambos peers, conociendo el syphrax_id del otro y el
 * freq_secret común, llegan al mismo pseudónimo y por tanto pueden enrutarse
 * mensajes mutuamente sin que el server entienda la relación.
 */
export function pseudonymOf(peerSyphraxId: string, freqSecret: string): string {
  const cacheKey = freqSecret + ' ' + peerSyphraxId;
  const cached = pseudoCache.get(cacheKey);
  if (cached) return cached;
  const seed = cachedFreqKey(freqSecret);
  const info = new TextEncoder().encode(PSEUDO_INFO_PREFIX + peerSyphraxId);
  const material = crypto.hkdf(seed, new Uint8Array(0), info, 16);
  const result = base32NoPad(material);
  pseudoCache.set(cacheKey, result);
  return result;
}

/** Base32 RFC 4648 sin padding: 16 bytes → 26 chars. Username opaco apto para humans. */
function base32NoPad(bytes: Uint8Array): string {
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let bits = 0, value = 0, out = '';
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += ALPHABET[(value << (5 - bits)) & 31];
  return out;
}
